import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from "@medplum/fhirtypes";

import { format, isValid, parse, parseISO } from "date-fns";

import {
  buildEffectiveDateTimeFromUtcParts,
  getLocalDateString,
  localDateAndTimeToUTCDateAndTime,
  localTimeFromISO,
} from "@/utils/datetime";
import { getCurrentLocalTimeString } from "@/utils/timeUtils";

/**
 * Finds date/time child linkIds under a DateTime group.
 * Uses endsWith so `vitalDateTime` is never matched as the date or time child.
 */
export function findDateTimeGroupChildLinkIds(dateTimeGroup: QuestionnaireItem): {
  dateLinkId: string | undefined;
  timeLinkId: string | undefined;
} {
  const dateLinkId = dateTimeGroup.item?.find(
    (item) => item.linkId?.endsWith("Date") && !item.linkId.endsWith("DateTime"),
  )?.linkId;
  const timeLinkId = dateTimeGroup.item?.find(
    (item) => item.linkId?.endsWith("Time") && !item.linkId.endsWith("DateTime"),
  )?.linkId;
  return { dateLinkId, timeLinkId };
}

/**
 * Converts a date value (Date object or string) to a Date object
 * Handles date strings as local dates (not UTC) to avoid timezone issues
 */
function normalizeDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    // If it's already a Date, extract just the date parts to avoid timezone issues
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  }
  if (typeof dateValue === "string") {
    // Parse date string as local date using date-fns parse function
    // This ensures YYYY-MM-DD strings are parsed as local dates, not UTC
    if (/^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      const parsed = parse(dateValue.slice(0, 10), "yyyy-MM-dd", new Date());
      return isValid(parsed) ? parsed : null;
    }
    // Reject non-YYYY-MM-DD string formats to avoid environment-dependent parsing
    // and timezone inconsistencies
    return null;
  }
  return null;
}

/**
 * Extracts the raw hours and minutes from a time value (Date object or string).
 * The time picker may return a Date object representing the time in UTC.
 * This function extracts the hours/minutes as they were selected by the user
 * (ignoring the UTC interpretation).
 */
function extractLocalTime(timeValue: unknown): string | null {
  if (typeof timeValue === "string") {
    const timeParts = timeValue.split(":");
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0;

      // Convert UTC time string to local time
      const utcDate = new Date();
      utcDate.setUTCHours(hours, minutes, seconds, 0);

      return format(utcDate, "HH:mm:ss");
    }
    return null;
  }
  return null;
}

/**
 * Extracts date and time from parsed form values for postpartum questionnaires.
 * Searches for DateTime groups at multiple levels in the form value structure.
 *
 * Form values can follow patterns like:
 * - { formName: { formNameDateTime: { formNameDate: Date, formNameTime: string } } }
 * - { formNameDateTime: { formNameDate: Date, formNameTime: string } }
 */
export function extractDateTimeFromFormValues(
  parsedValues: Record<string, unknown>,
  questionnaireName?: string,
): { date: Date | null; time: string | null } {
  if (!parsedValues) return { date: null, time: null };

  // If questionnaireName is provided, try to find DateTime within that form's values first
  if (questionnaireName) {
    const formValues = parsedValues[questionnaireName] as Record<string, unknown> | undefined;
    if (formValues) {
      const dateTimeKey = Object.keys(formValues).find((key) => key.endsWith("DateTime"));
      if (dateTimeKey) {
        const dateTimeGroup = formValues[dateTimeKey] as Record<string, unknown> | undefined;
        if (dateTimeGroup) {
          const dateKey = Object.keys(dateTimeGroup).find((key) => key.endsWith("Date"));
          const timeKey = Object.keys(dateTimeGroup).find((key) => key.endsWith("Time"));
          const date = dateKey ? normalizeDate(dateTimeGroup[dateKey]) : null;
          const time = timeKey ? extractLocalTime(dateTimeGroup[timeKey]) : null;
          return { date, time };
        }
      }
    }
  }

  // Try to find DateTime at top level of parsedValues
  const topLevelDateTimeKey = Object.keys(parsedValues).find((key) => key.endsWith("DateTime"));
  if (topLevelDateTimeKey) {
    const dateTimeGroup = parsedValues[topLevelDateTimeKey] as Record<string, unknown> | undefined;
    if (dateTimeGroup) {
      const dateKey = Object.keys(dateTimeGroup).find((key) => key.endsWith("Date"));
      const timeKey = Object.keys(dateTimeGroup).find((key) => key.endsWith("Time"));
      const date = dateKey ? normalizeDate(dateTimeGroup[dateKey]) : null;
      const time = timeKey ? extractLocalTime(dateTimeGroup[timeKey]) : null;
      return { date, time };
    }
  }

  // Recursively search for DateTime at any level
  const findDateTime = (
    obj: Record<string, unknown>,
  ): { date: Date | null; time: string | null } => {
    for (const [key, value] of Object.entries(obj)) {
      if (key.endsWith("DateTime") && typeof value === "object" && value !== null) {
        const dateTimeGroup = value as Record<string, unknown>;
        const dateKey = Object.keys(dateTimeGroup).find((k) => k.endsWith("Date"));
        const timeKey = Object.keys(dateTimeGroup).find((k) => k.endsWith("Time"));
        const date = dateKey ? normalizeDate(dateTimeGroup[dateKey]) : null;
        const time = timeKey ? extractLocalTime(dateTimeGroup[timeKey]) : null;
        if (date || time) return { date, time };
      }
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        const result = findDateTime(value as Record<string, unknown>);
        if (result.date || result.time) return result;
      }
    }
    return { date: null, time: null };
  };

  return findDateTime(parsedValues);
}

/**
 * Rewrites each DateTime group under the questionnaire root so valueDate + valueTime are
 * UTC-consistent (same instant), using the user's local calendar date and wall time from the form.
 * Call before getQuestionnaireResponseFromFormValues when submitting labor/postpartum flowsheets.
 *
 * @param utcDateAnchors — Optional map from DateTime group linkId to FHIR valueDate (YYYY-MM-DD).
 *   Required when the time field still holds UTC `HH:mm:ss` after edit hydration (same instant as display).
 */
export function normalizeQuestionnaireDateTimeGroupsToUtcParts(
  questionnaire: Questionnaire,
  parsedValues: Record<string, unknown>,
  utcDateAnchors?: Record<string, string>,
): Record<string, unknown> {
  const rootItem = questionnaire.item?.[0];
  if (!rootItem?.item?.length) return parsedValues;

  const result = structuredClone(parsedValues) as Record<string, unknown>;
  const rootLinkId = rootItem.linkId;
  const rootValues = rootLinkId
    ? ((result[rootLinkId] as Record<string, unknown>) ?? result)
    : result;

  for (const item of rootItem.item) {
    if (!item.linkId?.includes("DateTime")) continue;
    const { dateLinkId, timeLinkId } = findDateTimeGroupChildLinkIds(item);
    if (!dateLinkId || !timeLinkId) continue;

    const dateTimeGroup = rootValues[item.linkId] as Record<string, unknown> | undefined;
    if (!dateTimeGroup) continue;

    const localDate = normalizeDate(dateTimeGroup[dateLinkId]);
    const timeRaw = dateTimeGroup[timeLinkId];
    const anchor = utcDateAnchors?.[item.linkId];
    let localTime: string | null = null;

    if (typeof timeRaw === "string" && anchor && /^\d{4}-\d{2}-\d{2}$/.test(anchor)) {
      const isoInstant = buildEffectiveDateTimeFromUtcParts(anchor, timeRaw);
      if (isoInstant) {
        localTime = localTimeFromISO(isoInstant);
      }
    }
    if (!localTime) {
      localTime = extractLocalTimeForNormalization(timeRaw);
    }
    // Quick-add can land date (autopopulate) without time in form values; default to now so
    // bots always receive both parts for Observation.effectiveDateTime.
    if (localDate && !localTime) {
      localTime = getCurrentLocalTimeString();
    }
    if (!localDate || !localTime) continue;

    const { date: utcDate, time: utcTime } = localDateAndTimeToUTCDateAndTime(localDate, localTime);
    dateTimeGroup[dateLinkId] = utcDate;
    dateTimeGroup[timeLinkId] = utcTime;
  }

  return result;
}

function extractLocalTimeForNormalization(timeValue: unknown): string | null {
  if (typeof timeValue === "string") {
    return extractLocalTime(timeValue);
  }
  if (timeValue instanceof Date && !isNaN(timeValue.getTime())) {
    return format(timeValue, "HH:mm:ss");
  }
  return null;
}

function normalizeStoredTimeToHhMmSs(valueTime: string): string {
  return valueTime.length >= 8 ? valueTime.slice(0, 8) : `${valueTime.slice(0, 5)}:00`;
}

/** Labor/postpartum edit: fix form date/time to match table; returns FHIR valueDate anchors for time display. */
export function hydrateLaborFlowsheetDateTimeFormValuesForEdit(
  questionnaire: Questionnaire,
  values: Record<string, unknown>,
  questionnaireResponse: QuestionnaireResponse,
): { values: Record<string, unknown>; dateTimeUtcDateAnchors: Record<string, string> } {
  const dateTimeUtcDateAnchors: Record<string, string> = {};
  const rootItem = questionnaire.item?.[0];
  if (!rootItem?.item?.length) return { values, dateTimeUtcDateAnchors };

  const result = structuredClone(values) as Record<string, unknown>;
  const rootLinkId = rootItem.linkId;
  const rootValues = rootLinkId
    ? ((result[rootLinkId] as Record<string, unknown>) ?? result)
    : result;

  const responseRootItems = questionnaireResponse.item?.[0]?.item ?? [];

  for (const item of rootItem.item) {
    if (!item.linkId?.includes("DateTime")) continue;
    const { dateLinkId, timeLinkId } = findDateTimeGroupChildLinkIds(item);
    if (!dateLinkId || !timeLinkId) continue;

    const groupResponse = responseRootItems.find((r) => r.linkId === item.linkId);
    if (!groupResponse?.item) continue;

    const dateResp = groupResponse.item.find((i) => i.linkId === dateLinkId);
    const timeResp = groupResponse.item.find((i) => i.linkId === timeLinkId);
    const dateStr = dateResp?.answer?.[0]?.valueDate;
    const timeStr = timeResp?.answer?.[0]?.valueTime;
    if (typeof dateStr !== "string" || typeof timeStr !== "string") continue;

    const iso = buildEffectiveDateTimeFromUtcParts(dateStr, timeStr);
    if (!iso) continue;

    const localDateKey = getLocalDateString(iso);
    if (!localDateKey) continue;

    const dateTimeGroup = rootValues[item.linkId] as Record<string, unknown> | undefined;
    if (!dateTimeGroup) continue;

    dateTimeGroup[dateLinkId] = parseISO(localDateKey);
    dateTimeGroup[timeLinkId] = normalizeStoredTimeToHhMmSs(timeStr);
    dateTimeUtcDateAnchors[item.linkId] = dateStr.slice(0, 10);
  }

  return { values: result, dateTimeUtcDateAnchors };
}
